/**
 * Build circular crop data URLs from face image + detection bboxes.
 */

function normalizeBbox(bbox) {
  if (!bbox) return null;
  if (Array.isArray(bbox) && bbox.length >= 4) {
    const [x1, y1, x2, y2] = bbox.map(Number);
    if (![x1, y1, x2, y2].every(Number.isFinite)) return null;
    return { x1, y1, x2, y2 };
  }
  if (typeof bbox === 'object') {
    const x1 = Number(bbox.x1 ?? bbox.left ?? bbox.x);
    const y1 = Number(bbox.y1 ?? bbox.top ?? bbox.y);
    const x2 = Number(bbox.x2 ?? (Number.isFinite(x1) && Number.isFinite(bbox.width) ? x1 + Number(bbox.width) : NaN));
    const y2 = Number(bbox.y2 ?? (Number.isFinite(y1) && Number.isFinite(bbox.height) ? y1 + Number(bbox.height) : NaN));
    if (![x1, y1, x2, y2].every(Number.isFinite)) return null;
    return { x1, y1, x2, y2 };
  }
  return null;
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    if (!src) {
      reject(new Error('No image'));
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Image load failed'));
    img.src = src;
  });
}

/**
 * @param {string} imageSrc
 * @param {object|array} bbox
 * @param {number} [size=160]
 * @returns {Promise<string|null>} data URL
 */
export async function cropConcernCircle(imageSrc, bbox, size = 160) {
  const box = normalizeBbox(bbox);
  if (!box || !imageSrc) return null;
  try {
    const img = await loadImage(imageSrc);
    const w = Math.max(1, box.x2 - box.x1);
    const h = Math.max(1, box.y2 - box.y1);
    const pad = Math.max(w, h) * 0.35;
    const cx = (box.x1 + box.x2) / 2;
    const cy = (box.y1 + box.y2) / 2;
    const side = Math.max(w, h) + pad * 2;
    let sx = Math.max(0, cx - side / 2);
    let sy = Math.max(0, cy - side / 2);
    let sw = Math.min(side, img.naturalWidth - sx);
    let sh = Math.min(side, img.naturalHeight - sy);
    if (sw < 8 || sh < 8) {
      sx = 0;
      sy = 0;
      sw = img.naturalWidth;
      sh = img.naturalHeight;
    }

    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();
    ctx.drawImage(img, sx, sy, sw, sh, 0, 0, size, size);
    return canvas.toDataURL('image/jpeg', 0.85);
  } catch (_) {
    return null;
  }
}

/**
 * Fit a full mask / face image into a circle (cover crop).
 * @param {string} imageSrc
 * @param {number} [size=160]
 * @returns {Promise<string|null>}
 */
export async function circleFitImage(imageSrc, size = 160) {
  if (!imageSrc) return null;
  try {
    const img = await loadImage(imageSrc);
    const iw = img.naturalWidth || img.width;
    const ih = img.naturalHeight || img.height;
    if (!iw || !ih) return null;
    const side = Math.min(iw, ih);
    const sx = (iw - side) / 2;
    const sy = (ih - side) / 2;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();
    ctx.drawImage(img, sx, sy, side, side, 0, 0, size, size);
    return canvas.toDataURL('image/jpeg', 0.88);
  } catch (_) {
    return null;
  }
}

/**
 * Pick best bbox for a concern from detections list.
 */
export function findBestBboxForConcern(detections, concernName) {
  const target = String(concernName || '').toLowerCase().replace(/\s+/g, '_');
  const list = Array.isArray(detections) ? detections : [];
  let best = null;
  let bestConf = -1;
  for (const d of list) {
    const name = String(d?.class_name || d?.className || '').toLowerCase().replace(/\s+/g, '_');
    if (!name) continue;
    const match = name === target
      || name.includes(target)
      || target.includes(name)
      || (target.includes('pigment') && name.includes('spot'))
      || (target.includes('dark') && name.includes('circle'));
    if (!match) continue;
    const conf = Number(d?.confidence ?? d?.bestConf ?? 0);
    if (conf >= bestConf) {
      bestConf = conf;
      best = d?.bbox || d?.box || null;
    }
  }
  return best;
}
